import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Users, Calendar, Clock, MessageCircle, Filter, 
  ChevronRight, Check, X, Send, User, Heart,
  AlertCircle, RefreshCw, ArrowLeft, Play, Pause
} from 'lucide-react';

const CATEGORY_LABELS = {
  anxiety: 'Anxiety Support',
  depression: 'Depression Support',
  grief: 'Grief & Loss',
  stress: 'Stress Management',
  relationships: 'Relationships',
  self_esteem: 'Self-Esteem',
  addiction: 'Addiction Recovery',
  trauma: 'Trauma Support',
  general_support: 'General Support'
};

const CATEGORY_COLORS = {
  anxiety: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  depression: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  grief: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  stress: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  relationships: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  self_esteem: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  addiction: 'bg-red-500/20 text-red-400 border-red-500/30',
  trauma: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  general_support: 'bg-green-500/20 text-green-400 border-green-500/30'
};

// Session Card Component
const SessionCard = ({ session, onJoin, onLeave, onView }) => {
  const scheduledAt = new Date(session.scheduled_at);
  const isToday = scheduledAt.toDateString() === new Date().toDateString();
  const isTomorrow = scheduledAt.toDateString() === new Date(Date.now() + 86400000).toDateString();
  
  const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : scheduledAt.toLocaleDateString();
  const timeLabel = scheduledAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div 
      className={`bg-card border rounded-xl p-5 hover:border-primary/50 transition-all ${
        session.status === 'live' ? 'border-green-500/50 bg-green-500/5' : 'border-border'
      }`}
      data-testid={`session-card-${session.id}`}
    >
      {/* Status Badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${CATEGORY_COLORS[session.category] || 'bg-muted text-muted-foreground'}`}>
          {CATEGORY_LABELS[session.category] || session.category}
        </span>
        {session.status === 'live' && (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs animate-pulse">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            LIVE
          </span>
        )}
      </div>

      {/* Title & Description */}
      <h3 className="text-lg font-semibold text-foreground mb-2">{session.title}</h3>
      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{session.description}</p>

      {/* Info Row */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {dateLabel}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {timeLabel}
        </span>
        <span className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {session.participant_count}/{session.max_participants}
        </span>
      </div>

      {/* Tags */}
      {session.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {session.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="px-2 py-0.5 bg-muted rounded text-xs text-muted-foreground">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {session.is_joined ? (
          <>
            <button
              onClick={() => onView(session)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-lg hover:opacity-90 font-medium"
            >
              <MessageCircle className="w-4 h-4" />
              {session.status === 'live' ? 'Enter Session' : 'View Details'}
            </button>
            <button
              onClick={() => onLeave(session.id)}
              className="p-2.5 bg-muted text-muted-foreground rounded-lg hover:bg-destructive/20 hover:text-destructive transition-colors"
              title="Leave session"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => onJoin(session.id)}
            disabled={session.is_full}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary/20 text-primary rounded-lg hover:bg-primary hover:text-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {session.is_full ? (
              <>Session Full</>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Join Session
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Session Room Component (Chat Interface)
const SessionRoom = ({ session, onBack }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const messagesEndRef = React.useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const params = lastFetch ? `?since=${lastFetch}` : '';
      const response = await api.get(`/group-therapy/sessions/${session.id}/messages${params}`);
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const filtered = newMessages.filter(m => !existingIds.has(m.id));
          return [...prev, ...filtered];
        });
        setLastFetch(newMessages[newMessages.length - 1].timestamp);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [session.id, lastFetch]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await api.post(`/group-therapy/sessions/${session.id}/message`, {
        content: newMessage,
        is_anonymous: isAnonymous
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col bg-card rounded-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-muted rounded-lg">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="font-semibold text-foreground">{session.title}</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{session.participant_count} participants</span>
                {session.status === 'live' && (
                  <span className="flex items-center gap-1 text-green-400">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet. Be the first to share!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div 
              key={msg.id}
              className={`flex ${msg.user_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                msg.user_id === user?.user_id 
                  ? 'bg-primary text-white rounded-br-md' 
                  : 'bg-muted text-foreground rounded-bl-md'
              }`}>
                <p className="text-xs opacity-70 mb-1">
                  {msg.display_name}
                  {msg.is_anonymous && ' (Anonymous)'}
                </p>
                <p className="text-sm">{msg.content}</p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-border bg-muted/30">
        <form onSubmit={sendMessage} className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsAnonymous(!isAnonymous)}
            className={`p-2 rounded-lg transition-colors ${
              isAnonymous ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
            }`}
            title={isAnonymous ? 'Posting anonymously' : 'Posting with name'}
          >
            <User className="w-5 h-5" />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isAnonymous ? "Share anonymously..." : "Share your thoughts..."}
            className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            disabled={session.status === 'completed'}
          />
          <button
            type="submit"
            disabled={sending || !newMessage.trim() || session.status === 'completed'}
            className="p-3 bg-primary text-white rounded-xl hover:opacity-90 disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {isAnonymous ? 'Your name is hidden from other participants' : `Posting as ${user?.full_name}`}
        </p>
      </div>
    </div>
  );
};

// Main Component
const GroupTherapy = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('upcoming_only', 'true');
      
      const response = await api.get(`/group-therapy/sessions?${params.toString()}`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const fetchMySessions = useCallback(async () => {
    try {
      const response = await api.get('/group-therapy/sessions/my');
      setMySessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch my sessions:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/group-therapy/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchMySessions();
    fetchCategories();
  }, [fetchSessions, fetchMySessions, fetchCategories]);

  const handleJoin = async (sessionId) => {
    try {
      await api.post(`/group-therapy/sessions/${sessionId}/join`);
      toast.success('Successfully joined the session!');
      fetchSessions();
      fetchMySessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join session');
    }
  };

  const handleLeave = async (sessionId) => {
    try {
      await api.post(`/group-therapy/sessions/${sessionId}/leave`);
      toast.success('Left the session');
      fetchSessions();
      fetchMySessions();
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave session');
    }
  };

  const handleViewSession = async (session) => {
    try {
      const response = await api.get(`/group-therapy/sessions/${session.id}`);
      setActiveSession(response.data);
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  // If viewing a session room
  if (activeSession) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <SessionRoom session={activeSession} onBack={() => setActiveSession(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="group-therapy-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2 flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Group Therapy Sessions
          </h1>
          <p className="text-muted-foreground">
            Join supportive group sessions with others who understand your journey
          </p>
        </div>

        {/* My Sessions Alert */}
        {mySessions.filter(s => s.status === 'live').length > 0 && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Play className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-green-400">You have a session in progress!</p>
                <p className="text-sm text-muted-foreground">
                  {mySessions.filter(s => s.status === 'live')[0].title}
                </p>
              </div>
              <button
                onClick={() => handleViewSession(mySessions.filter(s => s.status === 'live')[0])}
                className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600"
              >
                Join Now
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'upcoming' ? 'bg-primary text-white' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            Upcoming Sessions
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'my' ? 'bg-primary text-white' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            My Sessions ({mySessions.length})
          </button>
        </div>

        {/* Category Filter */}
        {activeTab === 'upcoming' && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                !selectedCategory ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-muted/70'
              }`}
            >
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCategory === cat.value 
                    ? 'bg-primary text-white' 
                    : `${CATEGORY_COLORS[cat.value]?.split(' ')[0] || 'bg-muted'} hover:opacity-80`
                }`}
              >
                {cat.label} ({cat.count})
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {activeTab === 'upcoming' && (
              sessions.length === 0 ? (
                <div className="text-center py-16 bg-card rounded-xl border border-border">
                  <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-foreground mb-2">No Sessions Scheduled</h3>
                  <p className="text-muted-foreground">Check back later for upcoming group sessions</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      onJoin={handleJoin}
                      onLeave={handleLeave}
                      onView={handleViewSession}
                    />
                  ))}
                </div>
              )
            )}

            {activeTab === 'my' && (
              mySessions.length === 0 ? (
                <div className="text-center py-16 bg-card rounded-xl border border-border">
                  <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-foreground mb-2">No Sessions Joined</h3>
                  <p className="text-muted-foreground mb-4">Join a session to connect with others</p>
                  <button
                    onClick={() => setActiveTab('upcoming')}
                    className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:opacity-90"
                  >
                    Browse Sessions
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mySessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={{...session, is_joined: true}}
                      onJoin={handleJoin}
                      onLeave={handleLeave}
                      onView={handleViewSession}
                    />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupTherapy;
